import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceCost, 
  CostStats, 
  CostFilters 
} from '../types/maintenance-costs.types';

export const MaintenanceCostsService = {
  /**
   * Lấy danh sách chi phí
   */
  async getCosts(companyId: string, filters: CostFilters = {}): Promise<{ data: MaintenanceCost[], total: number }> {
    let query = supabase
      .from('maintenance_costs')
      .select(`
        *,
        project:projects(id, name),
        work_order:work_orders(id, work_order_code, title),
        recorder:user_profiles!recorded_by(id, full_name, avatar_url:avatar_path),
        approver:user_profiles!approved_by(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.type && filters.type !== 'all') query = query.eq('cost_type', filters.type);
    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.dateRange) {
      query = query.gte('cost_date', filters.dateRange.start).lte('cost_date', filters.dateRange.end);
    }
    if (filters.search) {
      query = query.or(`description.ilike.%${filters.search}%,reference_code.ilike.%${filters.search}%`);
    }

    query = query.order('cost_date', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceCost[],
      total: count || 0
    };
  },

  /**
   * Chi tiết chi phí
   */
  async getCostDetail(id: string): Promise<MaintenanceCost> {
    const { data, error } = await supabase
      .from('maintenance_costs')
      .select(`
        *,
        project:projects(id, name),
        work_order:work_orders(id, work_order_code, title),
        recorder:user_profiles!recorded_by(id, full_name, avatar_url:avatar_path),
        approver:user_profiles!approved_by(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenanceCost;
  },

  /**
   * Thống kê tài chính
   */
  async getCostStats(companyId: string): Promise<CostStats> {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    
    const { data, error } = await supabase
      .from('maintenance_costs')
      .select('cost_type, total_amount, cost_date')
      .eq('company_id', companyId)
      .eq('status_code', 'approved');

    if (error) throw error;

    const thisMonth = data.filter(c => c.cost_date >= firstDayOfMonth);
    const total = thisMonth.reduce((sum, c) => sum + c.total_amount, 0);
    
    return {
      totalCostThisMonth: total,
      laborCost: thisMonth.filter(c => c.cost_type === 'labor').reduce((sum, c) => sum + c.total_amount, 0),
      materialCost: thisMonth.filter(c => c.cost_type === 'material').reduce((sum, c) => sum + c.total_amount, 0),
      equipmentCost: thisMonth.filter(c => c.cost_type === 'equipment').reduce((sum, c) => sum + c.total_amount, 0),
      budgetUtilization: total > 0 ? (total / 500000000) * 100 : 0, // Giả sử ngân sách 500tr
    };
  },

  /**
   * Phê duyệt chi phí
   */
  async approveCost(id: string, approverId: string): Promise<void> {
    const { error } = await supabase
      .from('maintenance_costs')
      .update({
        status_code: 'approved',
        approved_by: approverId,
        approved_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * CRUD
   */
  async createCost(data: Partial<MaintenanceCost>): Promise<MaintenanceCost> {
    const { data: result, error } = await supabase
      .from('maintenance_costs')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceCost;
  }
};
